import { latitudeParallelReferenceSchema, latitudeParallelError, type LatitudeParallelReference } from './latitude-parallel.js';
export { circleDiameterPoints } from './circle-geometry.js';
import { validPolygonPoints } from './polygon-validation.js';
import { aiDrawingLayerActionSchema, type AiDrawingLayerAction } from './ai-map-drawing-layer.js';
import { aiDataLayerEditSchema, aiDataLayerDeleteSchema, type AiDataLayerAction } from './ai-map-data-layer.js';
import type { AiMapMeasurement } from './ai-map-measurement.js';
import type { AiMapPathGeometry } from './ai-map-path-geometry.js';
import type { DrawingGeographicSource } from './drawing-geographic-source.js';
import type { TravelTimeArea } from './travel-time-area.js';
import type { AiMapTaskResult } from './ai-map-result.js';
import type { SchemaInput } from './schema-input.js';
import { isNumber, isObject, isString } from './runtime-values.js';
import { MAX_DATA_LAYER_FEATURES, MAX_DATA_LAYER_NAME_LENGTH, type SupportedGeoJsonGeometryType } from './data-layer.js';
export const AI_MAP_FREE_MONTHLY_CREDITS = 5;
export const AI_MAP_PRO_MONTHLY_CREDITS = 500;
export const AI_MAP_MAX_ACTIONS = 600;
export const AI_MAP_MAX_MESSAGE_CHARACTERS = 4000;
export const AI_MAP_MAX_REPLY_CHARACTERS = 24_000;
export const AI_MAP_MAX_POINTS_PER_DRAWING = 50_000;
export const AI_MAP_MAX_POLYGON_POINTS = 2_000;
/** Optional map URLs are logged for context, but are not needed to make a plan. */
export const AI_MAP_MAX_URL_CHARACTERS = 20_000;

export type AiMapAction =
  | AiDrawingLayerAction
  | AiDataLayerAction
  | { type: 'clear' }
  | { type: 'basemap'; basemap: 'osm' | 'carto' | 'opentopo' }
  | { type: 'title'; title: string }
  | { type: 'delete'; drawingIds: string[] }
  | { type: 'set_text'; drawingIds: string[]; text: string }
  | { type: 'set_marker_label'; drawingIds: string[]; label: string }
  | { type: 'recolor'; drawingIds: string[]; color: string }
  | { type: 'set_style'; drawingIds: string[]; color?: string; width?: number; fillColor?: string; fillOpacity?: number }
  | { type: 'convert_to_polygon'; drawingId: string; fillColor?: string; fillOpacity?: number }
  | { type: 'viewport'; center?: { lat: number; lng: number }; zoom: number }
  | { type: 'line'; points: Array<{ lat: number; lng: number }>; color?: string; width?: number; dashed?: boolean; straight?: boolean; latitudeParallel?: LatitudeParallelReference }
  | { type: 'freehand'; points: Array<{ lat: number; lng: number }>; color?: string; width?: number; source?: 'route' | 'geometry' }
  | { type: 'polygon'; travelTime?: TravelTimeArea; geographicSource?: DrawingGeographicSource; points: Array<{ lat: number; lng: number }>; color?: string; width?: number; fillColor?: string; fillOpacity?: number }
  | { type: 'circle'; center: { lat: number; lng: number }; radiusKm: number; color?: string; width?: number }
  | { type: 'text'; point: { lat: number; lng: number }; text: string; color?: string; width?: number }
  | { type: 'marker'; point: { lat: number; lng: number }; color?: string; width?: number; label?: string }
  /** Client-visible reference to a private, validated data-layer artifact. */
  | { type: 'add_data_layer'; artifactId: string; layerId: string; name: string; featureCount: number; bounds: [number, number, number, number] | null }
  /** Server-only reference to a trusted routing-tool result. It is expanded to lines before reaching the editor. */
  | { type: 'isochrone'; toolCallId: string; color?: string; width?: number; fillColor?: string; fillOpacity?: number }
  /** Server-only reference to a trusted route result. It is expanded to a line before reaching the editor. */
  | { type: 'route'; toolCallId: string; color?: string; width?: number; dashed?: boolean }
  /** Server-only reference to trusted boundary or dataset geometry. */
  | { type: 'boundary'; toolCallId: string; color?: string; width?: number; fillColor?: string; fillOpacity?: number }
  /** Server-only reference to trusted OpenStreetMap line or area geometry. */
  | { type: 'geometry'; toolCallId: string; color?: string; width?: number; fillColor?: string; fillOpacity?: number };

export interface AiMapPlan {
  /** Absent on legacy plans. Only the server can classify a validated task. */
  task?: AiMapTaskResult;
  reply: string;
  actions: AiMapAction[];
  /** Server-derived evidence for the change receipt. Model output cannot set this field. */
  receipt?: {
    sources: Array<'place' | 'route' | 'boundary' | 'dataset' | 'map_data' | 'travel_time'>;
    targetPlace?: string;
  };
}

/** A trusted named-place result the user must confirm before a map edit. */
export interface AiMapPlaceCandidate {
  name: string;
  lat: number;
  lng: number;
  primary?: string;
  secondary?: string;
  type?: string;
}

export interface AiMapPlaceChoice {
  query: string;
  candidate: AiMapPlaceCandidate;
}

export interface AiMapNeedsPlaceChoice {
  query: string;
  candidates: AiMapPlaceCandidate[];
}

export interface AiMapDrawingContext {
  page?: import('./ai-map-page-context.js').AiMapPageContext;
  inventory?: { version: 1; dataLayerCount: number; complete: boolean };
  drawingCount: number;
  drawingLayers?: import('./drawing-layer.js').DrawingLayer[];
  dataLayers?: Array<{
    id: string;
    name: string;
    visible: boolean;
    featureCount: number;
    features?: Array<{ id: string; name: string; selected?: boolean; geometry?: import('./data-layer.js').DataGeometry }>;
    style?: import('./data-layer.js').DataLayerStyle;
    display?: import('./data-layer.js').DataLayerDisplay;
    geometryTypes: Array<SupportedGeoJsonGeometryType | 'GeometryCollection' | 'null'>;
  }>;
  /** Primary id of the newest visible drawing, independent of selection-first ordering. */
  mostRecentDrawingId?: string;
  recentChangeDrawingIds?: string[];
  recentChangeKind?: 'created' | 'edited' | 'mixed';
  basemap?: 'osm' | 'carto' | 'opentopo';
  mapTitle?: string;
  canRenameMap?: boolean;
  drawings: Array<{
    id: string;
    /** All strokes that make up this visible item, e.g. a pin and its label. */
    deleteIds: string[];
    layerId?: string;
    type: 'freehand' | 'line' | 'arrow' | 'circle' | 'polygon' | 'marker' | 'text';
    points: Array<{ lat: number; lng: number }>;
    fullPoints?: Array<{ lat: number; lng: number }>;
    pathGeometry?: AiMapPathGeometry;
    geographicSource?: DrawingGeographicSource;
    color: string;
    width?: number;
    fillColor?: string;
    fillOpacity?: number;
    /** Exact path length from the complete local geometry, before context point compaction. */
    lengthKm?: number;
    measurement?: AiMapMeasurement;
    travelTime?: TravelTimeArea;
    label?: string;
    /** True when this item is selected in the editor at request time. */
    selected?: boolean;
  }>;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const MAX_COORDINATE = 180;
type RawPoint = { lat?: unknown; lng?: unknown };
type RawStyle = { color?: unknown; width?: unknown };
type RawLineAction = RawStyle & { type: 'line'; points?: unknown; dashed?: unknown; straight?: unknown; latitudeParallel?: unknown };
type RawFreehandAction = RawStyle & { type: 'freehand'; points?: unknown };
type RawPolygonAction = RawStyle & { type: 'polygon'; points?: unknown; fillColor?: unknown; fillOpacity?: unknown };
type RawCircleAction = RawStyle & { type: 'circle'; center?: unknown; radiusKm?: unknown };
type RawTextAction = RawStyle & { type: 'text'; point?: unknown; text?: unknown };
type RawMarkerAction = RawStyle & { type: 'marker'; point?: unknown; label?: unknown };
type RawAddDataLayerAction = { type: 'add_data_layer'; artifactId?: unknown; layerId?: unknown; name?: unknown; featureCount?: unknown; bounds?: SchemaInput };
type RawToolAction = RawStyle & { type: 'isochrone' | 'route' | 'boundary' | 'geometry'; toolCallId?: unknown; fillColor?: unknown; fillOpacity?: unknown };
type RawDeleteAction = { type: 'delete'; drawingIds?: unknown };
type RawSetStyleAction = RawStyle & { type: 'set_style'; drawingIds?: unknown; fillColor?: unknown; fillOpacity?: unknown };
type RawTextEditAction = { type: 'set_text'; drawingIds?: unknown; text?: unknown };
type RawMarkerLabelAction = { type: 'set_marker_label'; drawingIds?: unknown; label?: unknown };
type RawRecolorAction = { type: 'recolor'; drawingIds?: unknown; color?: unknown };
type RawConvertToPolygonAction = { type: 'convert_to_polygon'; drawingId?: unknown; fillColor?: unknown; fillOpacity?: unknown };
type RawViewportAction = { type: 'viewport'; center?: unknown; zoom?: unknown };
type RawBasemapAction = { type: 'basemap'; basemap?: unknown };
type RawTitleAction = { type: 'title'; title?: unknown };
type RawAction = AiDrawingLayerAction | AiDataLayerAction | { type: 'clear' } | RawBasemapAction | RawTitleAction | RawDeleteAction | RawTextEditAction | RawMarkerLabelAction | RawRecolorAction | RawSetStyleAction | RawConvertToPolygonAction | RawViewportAction | RawLineAction | RawFreehandAction | RawPolygonAction | RawCircleAction | RawMarkerAction | RawTextAction | RawAddDataLayerAction | RawToolAction;
type RawPlan = { reply?: unknown; actions?: unknown };

function rawAction(value: SchemaInput): RawAction | null {
  if (!value || !isObject(value)) return null;
  // SAFETY: The adjacent boundary checks validate the fields that this code reads from the value.
  const type = (value as { type?: unknown }).type;
  return isString(type) && ['set_text', 'reorder_drawing_layers', 'create_drawing_layer', 'rename_drawing_layer', 'move_drawings_to_layer', 'update_data_layer', 'delete_data_layer', 'clear', 'basemap', 'title', 'delete', 'recolor', 'set_marker_label', 'set_style', 'convert_to_polygon', 'viewport', 'line', 'freehand', 'polygon', 'circle', 'marker', 'text', 'add_data_layer', 'isochrone', 'route', 'boundary', 'geometry'].includes(type)
    // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
    ? /* SAFETY: The adjacent boundary checks validate every field that this code reads. */ value as RawAction
    : null;
}


function point(value: SchemaInput): { lat: number; lng: number } | null {
  if (!value || !isObject(value)) return null;
  // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
  const candidate = value as RawPoint;
  const lat = candidate.lat;
  const lng = candidate.lng;
  if (!isNumber(lat) || !isNumber(lng) || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > MAX_COORDINATE) return null;
  return { lat, lng };
}

const POLYGON_EPSILON = 1e-10;

function samePoint(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  return Math.abs(a.lat - b.lat) <= POLYGON_EPSILON && Math.abs(a.lng - b.lng) <= POLYGON_EPSILON;
}


function style(value: RawStyle) {
  const color = isString(value.color) && HEX_COLOR.test(value.color) ? value.color : undefined;
  const width = isNumber(value.width) && Number.isFinite(value.width) ? Math.min(24, Math.max(1, value.width)) : undefined;
  return { color, width };
}

function fillStyle(value: { fillColor?: unknown; fillOpacity?: unknown }): { fillColor?: string; fillOpacity?: number } | null {
  const fillColor = isString(value.fillColor) && HEX_COLOR.test(value.fillColor) ? value.fillColor : undefined;
  if (value.fillColor !== undefined && !fillColor) return null;
  const fillOpacity = isNumber(value.fillOpacity) && Number.isFinite(value.fillOpacity) && value.fillOpacity >= 0 && value.fillOpacity <= 1
    ? value.fillOpacity
    : undefined;
  return value.fillOpacity !== undefined && fillOpacity === undefined ? null : { fillColor, fillOpacity };
}

function lineAction(action: RawLineAction, base: { color?: string; width?: number }): AiMapAction | null {
  if (!Array.isArray(action.points) || action.points.length < 2 || action.points.length > AI_MAP_MAX_POINTS_PER_DRAWING) return null;
  const points = action.points.map(point);
  if (points.some((item) => item === null)) return null;
  const reference = action.latitudeParallel === undefined ? undefined : latitudeParallelReferenceSchema.safeParse(action.latitudeParallel);
  if (reference && (!reference.success || action.straight !== true || latitudeParallelError(points.filter((item) => item !== null), reference.data))) return null;
  // SAFETY: The adjacent array check validates the collection and its elements before use.
  const line: AiMapAction = { type: 'line', points: points as Array<{ lat: number; lng: number }>, ...base, dashed: action.dashed === true, straight: action.straight === true };
  if (reference?.success) line.latitudeParallel = reference.data;
  return line;
}

function freehandAction(action: RawFreehandAction, base: { color?: string; width?: number }): AiMapAction | null {
  if (!Array.isArray(action.points) || action.points.length < 2 || action.points.length > AI_MAP_MAX_POINTS_PER_DRAWING) return null;
  const points = action.points.map(point);
  // SAFETY: The adjacent array check validates the collection and its elements before use.
  return points.some((item) => item === null) ? null : { type: 'freehand', points: points as Array<{ lat: number; lng: number }>, ...base };
}

function polygonAction(action: RawPolygonAction, base: { color?: string; width?: number }): AiMapAction | null {
  if (!Array.isArray(action.points) || action.points.length < 3 || action.points.length > AI_MAP_MAX_POLYGON_POINTS + 1) return null;
  const parsed = action.points.map(point);
  if (parsed.some((item) => item === null)) return null;
  // SAFETY: The adjacent array check validates the collection and its elements before use.
  const points = parsed as Array<{ lat: number; lng: number }>;
  if (points.length > 3 && samePoint(points[0], points[points.length - 1])) points.pop();
  if (points.length > AI_MAP_MAX_POLYGON_POINTS || !validPolygonPoints(points)) return null;
  const fill = fillStyle(action);
  // SAFETY: The adjacent array check validates the collection and its elements before use.
  return fill ? { type: 'polygon', points: points as Array<{ lat: number; lng: number }>, ...base, ...fill } : null;
}

function circleAction(action: RawCircleAction, base: { color?: string; width?: number }): AiMapAction | null {
  const center = point(action.center);
  if (!center || !isNumber(action.radiusKm) || !Number.isFinite(action.radiusKm) || action.radiusKm <= 0 || action.radiusKm > 5000) return null;
  return { type: 'circle', center, radiusKm: action.radiusKm, ...base };
}

function markerAction(action: RawMarkerAction, base: { color?: string; width?: number }): AiMapAction | null {
  const markerPoint = point(action.point);
  if (!markerPoint) return null;
  const label = isString(action.label) && action.label.trim().length > 0 ? action.label.trim().slice(0, 120) : undefined;
  return { type: 'marker', point: markerPoint, ...base, label };
}

function textAction(action: RawTextAction, base: { color?: string; width?: number }): AiMapAction | null {
  const anchor = point(action.point);
  const text = isString(action.text) ? action.text.trim() : '';
  return anchor && text && text.length <= 500 ? { type: 'text', point: anchor, text, ...base } : null;
}

function dataLayerBounds(value: SchemaInput): [number, number, number, number] | null | undefined {
  if (value === null) return null;
  if (!Array.isArray(value) || value.length !== 4 || value.some((item) => !isNumber(item) || !Number.isFinite(item))) return undefined;
  const [minLng, minLat, maxLng, maxLat] = value;
  if (Math.abs(minLng) > 180 || Math.abs(maxLng) > 180 || Math.abs(minLat) > 90 || Math.abs(maxLat) > 90 || minLng > maxLng || minLat > maxLat) return undefined;
  return [minLng, minLat, maxLng, maxLat];
}

function addDataLayerAction(action: RawAddDataLayerAction): AiMapAction | null {
  const artifactId = isString(action.artifactId) ? action.artifactId.trim() : '';
  const layerId = isString(action.layerId) ? action.layerId.trim() : '';
  const name = isString(action.name) ? action.name.trim() : '';
  const bounds = dataLayerBounds(action.bounds);
  if (!artifactId || artifactId.length > 128 || !layerId || layerId.length > 100 || !name || name.length > MAX_DATA_LAYER_NAME_LENGTH) return null;
  if (!isNumber(action.featureCount) || !Number.isInteger(action.featureCount) || action.featureCount < 1 || action.featureCount > MAX_DATA_LAYER_FEATURES || bounds === undefined) return null;
  return { type: 'add_data_layer', artifactId, layerId, name, featureCount: action.featureCount, bounds };
}

function isochroneAction(action: RawToolAction, base: { color?: string; width?: number }): AiMapAction | null {
  const toolCallId = isString(action.toolCallId) ? action.toolCallId.trim() : '';
  const fill = fillStyle(action);
  return toolCallId.length > 0 && toolCallId.length <= 128 && fill ? { type: 'isochrone', toolCallId, ...base, ...fill } : null;
}

function trustedGeometryAction(action: RawToolAction, type: 'route' | 'boundary' | 'geometry', base: { color?: string; width?: number }): AiMapAction | null {
  const toolCallId = isString(action.toolCallId) ? action.toolCallId.trim() : '';
  const fill = type === 'route' ? {} : fillStyle(action);
  // SAFETY: The adjacent allowlist, normalizer, or equality check limits this value to the stated union.
  return toolCallId.length > 0 && toolCallId.length <= 128 && fill ? { type, toolCallId, ...base, ...fill } as AiMapAction : null;
}

function deleteAction(action: RawDeleteAction): AiMapAction | null {
  if (!Array.isArray(action.drawingIds) || action.drawingIds.length < 1 || action.drawingIds.length > AI_MAP_MAX_ACTIONS) return null;
  const drawingIds = action.drawingIds.filter((id): id is string => isString(id) && id.length > 0 && id.length <= 128);
  if (drawingIds.length !== action.drawingIds.length || new Set(drawingIds).size !== drawingIds.length) return null;
  return { type: 'delete', drawingIds };
}

function recolorAction(action: RawRecolorAction): AiMapAction | null {
  if (!Array.isArray(action.drawingIds) || action.drawingIds.length < 1 || action.drawingIds.length > AI_MAP_MAX_ACTIONS) return null;
  const drawingIds = action.drawingIds.filter((id): id is string => isString(id) && id.length > 0 && id.length <= 128);
  if (drawingIds.length !== action.drawingIds.length || new Set(drawingIds).size !== drawingIds.length) return null;
  if (!isString(action.color) || !HEX_COLOR.test(action.color)) return null;
  return { type: 'recolor', drawingIds, color: action.color };
}

function markerLabelAction(action: RawMarkerLabelAction): AiMapAction | null {
  const target = deleteAction({ type: 'delete', drawingIds: action.drawingIds });
  if (!target || !('drawingIds' in target) || !isString(action.label) || action.label.trim().length > 120) return null;
  return { type: 'set_marker_label', drawingIds: target.drawingIds, label: action.label.trim() };
}

function textEditAction(action: RawTextEditAction): AiMapAction | null {
  const target = deleteAction({ type: 'delete', drawingIds: action.drawingIds });
  if (!target || !('drawingIds' in target) || !isString(action.text) || !action.text.trim() || action.text.trim().length > 500) return null;
  return { type: 'set_text', drawingIds: target.drawingIds, text: action.text.trim() };
}

function setStyleAction(action: RawSetStyleAction): AiMapAction | null {
  const target = deleteAction({ type: 'delete', drawingIds: action.drawingIds });
  const fill = fillStyle(action);
  if (!target || !('drawingIds' in target) || !fill) return null;
  if (action.color !== undefined && (!isString(action.color) || !HEX_COLOR.test(action.color))) return null;
  if (action.width !== undefined && (!isNumber(action.width) || !Number.isFinite(action.width) || action.width < 1 || action.width > 24)) return null;
  const attributes = Object.fromEntries(Object.entries({ ...style(action), ...fill }).filter(([, value]) => value !== undefined));
  if (!Object.keys(attributes).length) return null;
  return { type: 'set_style', drawingIds: target.drawingIds, ...attributes };
}

function convertToPolygonAction(action: RawConvertToPolygonAction): AiMapAction | null {
  const drawingId = isString(action.drawingId) ? action.drawingId.trim() : '';
  const fill = fillStyle(action);
  return drawingId.length > 0 && drawingId.length <= 128 && fill
    ? { type: 'convert_to_polygon', drawingId, ...fill }
    : null;
}

function viewportAction(action: RawViewportAction): AiMapAction | null {
  if (!isNumber(action.zoom) || !Number.isFinite(action.zoom) || action.zoom < 2 || action.zoom > 24) return null;
  if (action.center === undefined) return { type: 'viewport', zoom: action.zoom };
  const center = point(action.center);
  return center ? { type: 'viewport', center, zoom: action.zoom } : null;
}

function basemapAction(action: RawBasemapAction): AiMapAction | null {
  return action.basemap === 'osm' || action.basemap === 'carto' || action.basemap === 'opentopo'
    ? { type: 'basemap', basemap: action.basemap }
    : null;
}

function titleAction(action: RawTitleAction): AiMapAction | null {
  const title = isString(action.title) ? action.title.trim() : '';
  return title && title.length <= 120 ? { type: 'title', title } : null;
}

function parsePresentationAction(action: RawAction, actions: AiMapAction[]): AiMapAction | null | undefined {
  if (action.type === 'basemap') return actions.some((item) => item.type === 'basemap') ? null : basemapAction(action);
  if (action.type === 'title') return actions.some((item) => item.type === 'title') ? null : titleAction(action);
  return undefined;
}

function parseExistingDrawingAction(action: RawAction, actions: AiMapAction[]): AiMapAction | null | undefined {
  if (['reorder_drawing_layers', 'create_drawing_layer', 'rename_drawing_layer', 'move_drawings_to_layer'].includes(action.type)) {
    const parsed = aiDrawingLayerActionSchema.safeParse(action);
    return parsed.success ? parsed.data : null;
  }
  if (action.type === 'update_data_layer' || action.type === 'delete_data_layer') {
    const parsed = (action.type === 'update_data_layer' ? aiDataLayerEditSchema : aiDataLayerDeleteSchema).safeParse(action);
    return parsed.success ? parsed.data : null;
  }
  if (action.type === 'delete') return actions.some((item) => item.type === 'clear') ? null : deleteAction(action);
  if (action.type === 'set_text') return actions.some((item) => item.type === 'clear') ? null : textEditAction(action);
  if (action.type === 'set_marker_label') return actions.some((item) => item.type === 'clear') ? null : markerLabelAction(action);
  if (action.type === 'set_style') return actions.some((item) => item.type === 'clear') ? null : setStyleAction(action);
  if (action.type === 'recolor') return actions.some((item) => item.type === 'clear') ? null : recolorAction(action);
  if (action.type === 'convert_to_polygon') return actions.some((item) => item.type === 'clear') ? null : convertToPolygonAction(action);
  return undefined;
}

function parseAction(action: RawAction, actions: AiMapAction[]): AiMapAction | null {
  if (action.type === 'clear') return actions.length === 0 ? { type: 'clear' } : null;
  const presentation = parsePresentationAction(action, actions);
  if (presentation !== undefined) return presentation;
  const existingDrawing = parseExistingDrawingAction(action, actions);
  if (existingDrawing !== undefined) return existingDrawing;
  if (action.type === 'viewport') return actions.some((item) => item.type === 'viewport') ? null : viewportAction(action);
  // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
  const base = style(action as RawStyle);
  return action.type === 'line' ? lineAction(action, base)
    : action.type === 'freehand' ? freehandAction(action, base)
    : action.type === 'polygon' ? polygonAction(action, base)
    : action.type === 'circle' ? circleAction(action, base)
      : action.type === 'text' ? textAction(action, base)
      : action.type === 'marker' ? markerAction(action, base)
        : action.type === 'add_data_layer' ? addDataLayerAction(action)
        : action.type === 'isochrone' ? isochroneAction(action, base)
          : action.type === 'route' ? trustedGeometryAction(action, 'route', base)
            : action.type === 'boundary' ? trustedGeometryAction(action, 'boundary', base)
              : action.type === 'geometry' ? trustedGeometryAction(action, 'geometry', base) : null;
}

/** Validates one action before the server stages it for a single safe apply. */
export function parseAiMapAction(value: SchemaInput, existingActions: AiMapAction[] = []): AiMapAction | null {
  const action = rawAction(value);
  return action ? parseAction(action, existingActions) : null;
}

export function parseAiMapPlan(value: SchemaInput): AiMapPlan | null {
  if (!value || !isObject(value)) return null;
  // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
  const candidate = value as RawPlan;
  if (!isString(candidate.reply) || candidate.reply.trim().length === 0 || candidate.reply.length > AI_MAP_MAX_REPLY_CHARACTERS || !Array.isArray(candidate.actions) || candidate.actions.length > AI_MAP_MAX_ACTIONS) return null;
  const actions: AiMapAction[] = [];
  for (const raw of candidate.actions) {
    const parsed = parseAiMapAction(raw, actions);
    if (!parsed) return null;
    actions.push(parsed);
  }
  return { reply: candidate.reply.trim(), actions };
}
